import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, RefreshCw, Plus, Trash2, RotateCw, Link as LinkIcon, AlertTriangle } from "lucide-react";

const SOURCES = [
  { id: "booking",     label: "Booking.com" },
  { id: "airbnb",      label: "Airbnb" },
  { id: "vrbo",        label: "Vrbo / HomeAway" },
  { id: "manual",      label: "Altro" },
];

export default function IcalSync() {
  const [exportInfo, setExportInfo] = useState(null);
  const [feeds, setFeeds] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const emptyForm = { name: "", source: "booking", url: "", room_id: "" };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: ex }, { data: f }, { data: r }] = await Promise.all([
        api.get("/calendar/ical/url"),
        api.get("/calendar/feeds"),
        api.get("/rooms"),
      ]);
      setExportInfo(ex); setFeeds(f); setRooms(r);
      if (!form.room_id && r[0]) setForm(prev => ({ ...prev, room_id: r[0].id }));
    } catch (err) {
      if (err?.response?.status !== 402) toast.error(err?.response?.data?.detail || "Errore");
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("URL copiato"); }
    catch { toast.error("Impossibile copiare"); }
  };

  const rotate = async () => {
    if (!confirm("Rigenero l'URL pubblico. L'URL precedente smetterà di funzionare. Procedere?")) return;
    try {
      const { data } = await api.post("/calendar/ical/rotate");
      setExportInfo(data);
      toast.success("URL rigenerato. Aggiornalo su Booking/Airbnb.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.url || !form.room_id) { toast.error("Compila tutti i campi"); return; }
    try {
      await api.post("/calendar/feeds", form);
      toast.success("Feed aggiunto. Ora sincronizza per importare le prenotazioni.");
      setShowAdd(false); setForm({ ...emptyForm, room_id: rooms[0]?.id || "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const syncOne = async (id) => {
    try {
      const { data } = await api.post(`/calendar/feeds/${id}/sync`);
      toast.success(`Sincronizzato: ${data.events ?? 0} eventi`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore sync"); }
  };

  const syncAll = async () => {
    setSyncingAll(true);
    try {
      const { data } = await api.post("/calendar/feeds/sync-all");
      toast.success(`Sincronizzati ${data.feeds} feed`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSyncingAll(false); }
  };

  const remove = async (id) => {
    if (!confirm("Rimuovo il feed e tutte le prenotazioni-blocco importate da esso. Procedere?")) return;
    try {
      await api.delete(`/calendar/feeds/${id}`);
      toast.success("Feed rimosso");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="p-6 md:p-12 max-w-7xl">
      <p className="text-xs tracked text-blue-700 mb-2">CHANNEL MANAGER · iCal</p>
      <h1 className="font-heading font-semibold text-4xl tracking-tight mb-2">Sincronizzazione con Booking, Airbnb e altri portali</h1>
      <p className="text-slate-600 mb-10 max-w-prose">
        Tieni le date occupate allineate tra Xenia e i portali. <strong>Esporta</strong> il calendario di Xenia su ogni OTA per evitare doppie prenotazioni; <strong>importa</strong> i calendari OTA in Xenia per vedere tutto in un unico posto.
      </p>

      {/* Export side */}
      <section className="mb-12">
        <h2 className="font-heading font-semibold text-2xl mb-2">Esporta verso le OTA</h2>
        <p className="text-slate-600 mb-4 text-sm">Copia questo URL e incollalo in Booking Extranet / Airbnb / Vrbo come "calendario importato esterno". Aggiornano automaticamente.</p>

        <div className="border border-slate-200 bg-slate-50 p-4 flex flex-wrap items-center gap-3" data-testid="ical-export-url">
          <LinkIcon className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <code className="flex-1 min-w-[240px] text-xs break-all font-mono text-slate-700">
            {exportInfo?.url || "…"}
          </code>
          <button onClick={() => copy(exportInfo?.url)} disabled={!exportInfo}
            className="inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100" data-testid="ical-copy">
            <Copy className="w-4 h-4" /> Copia
          </button>
          <button onClick={rotate} className="inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100" data-testid="ical-rotate">
            <RotateCw className="w-4 h-4" /> Rigenera
          </button>
        </div>
      </section>

      {/* Import side */}
      <section>
        <header className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-semibold text-2xl">Importa dalle OTA</h2>
          <div className="flex gap-2">
            <button onClick={syncAll} disabled={feeds.length === 0 || syncingAll}
              className="inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50" data-testid="ical-sync-all">
              <RefreshCw className={`w-4 h-4 ${syncingAll ? "animate-spin" : ""}`} /> Sincronizza tutto
            </button>
            <button onClick={() => setShowAdd(s => !s)}
              className="inline-flex items-center gap-2 bg-stone-900 text-stone-50 px-3 py-1.5 text-sm rounded-lg hover:bg-stone-800" data-testid="ical-add-toggle">
              <Plus className="w-4 h-4" /> Aggiungi feed
            </button>
          </div>
        </header>

        {showAdd && (
          <form onSubmit={create} className="border border-slate-200 bg-white p-4 mb-4 grid md:grid-cols-4 gap-3 text-sm" data-testid="ical-add-form">
            <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} className="border border-slate-300 px-3 py-2 bg-white" data-testid="ical-source">
              {SOURCES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Etichetta (es. Booking · Suite Romeo)" className="border border-slate-300 px-3 py-2 bg-white" data-testid="ical-name" />
            <select value={form.room_id} onChange={e => setForm({ ...form, room_id: e.target.value })} className="border border-slate-300 px-3 py-2 bg-white" data-testid="ical-room">
              <option value="">— camera —</option>
              {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://… .ics" className="md:col-span-3 border border-slate-300 px-3 py-2 bg-white font-mono text-xs" data-testid="ical-url" />
            <button type="submit" className="bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-700" data-testid="ical-create">Aggiungi</button>
          </form>
        )}

        {loading ? (
          <p className="text-slate-500 text-sm">…</p>
        ) : feeds.length === 0 ? (
          <p className="text-slate-500 text-sm border border-dashed border-slate-300 p-6 bg-white" data-testid="ical-feeds-empty">
            Ancora nessun feed configurato.<br />
            Per <strong>Booking</strong>: Extranet → Tariffe e disponibilità → Sincronizza calendari → copia "Esporta calendario" e incollalo qui.<br />
            Per <strong>Airbnb</strong>: Annunci → Disponibilità → Sincronizza calendari → "Esporta calendario".
          </p>
        ) : (
          <div className="border border-slate-200 bg-white divide-y divide-stone-200" data-testid="ical-feeds-list">
            {feeds.map(f => (
              <div key={f.id} className="p-4 flex flex-wrap items-center gap-3 text-sm">
                <span className="inline-flex items-center px-2 py-0.5 text-[10px] tracked bg-slate-100 text-slate-600 rounded-lg">
                  {(SOURCES.find(s => s.id === f.source) || {}).label || f.source}
                </span>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-medium">{f.name}</div>
                  <div className="text-xs text-slate-500 break-all">{f.url}</div>
                </div>
                <span className="text-xs text-slate-500 whitespace-nowrap">
                  {f.last_sync_at
                    ? `Ultimo sync ${new Date(f.last_sync_at).toLocaleString("it-IT")} · ${f.last_sync_count ?? 0} eventi`
                    : "Mai sincronizzato"}
                </span>
                {f.last_sync_error && (
                  <span className="inline-flex items-center gap-1 text-xs text-red-700" title={f.last_sync_error}>
                    <AlertTriangle className="w-3 h-3" /> errore
                  </span>
                )}
                <button onClick={() => syncOne(f.id)} className="inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 hover:bg-slate-50" data-testid={`ical-sync-${f.id}`}>
                  <RefreshCw className="w-4 h-4" /> Sync
                </button>
                <button onClick={() => remove(f.id)} className="inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 hover:bg-slate-50 text-red-700" data-testid={`ical-delete-${f.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
